import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type UploadListItem } from "../../lib/api";
import { formatBytes } from "../../lib/format";

interface SourceLite {
  id: string;
  name: string;
  connectorId: string;
}

// UploadsSection lists every file the operator has uploaded — separated
// into "attached" (linked to a saved source) and "unattached" (orphans the
// janitor will purge after 24h). Admin-only; the route guard already
// rejects everyone else.
export function UploadsSection() {
  const qc = useQueryClient();

  const uploads = useQuery({
    queryKey: ["uploads"],
    queryFn: () => api.get<UploadListItem[]>("/api/v1/uploads"),
  });
  const sources = useQuery({
    queryKey: ["sources"],
    queryFn: () => api.get<SourceLite[]>("/api/v1/sources"),
  });

  const sourceById = useMemo(() => {
    const m = new Map<string, SourceLite>();
    (sources.data ?? []).forEach((s) => m.set(s.id, s));
    return m;
  }, [sources.data]);

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/uploads/${encodeURIComponent(id)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["uploads"] }),
  });

  const all = uploads.data ?? [];
  const attached = all.filter((u) => u.sourceId);
  const orphans = all.filter((u) => !u.sourceId);

  return (
    <div className="space-y-6">
      <section className="card overflow-hidden">
        <div className="border-b border-rule px-6 py-4">
          <h2 className="font-display text-[18px] font-semibold tracking-tight text-ink">
            Attached files
            <span className="ml-2 font-mono text-[12px] text-ash">{attached.length}</span>
          </h2>
          <p className="mt-1 text-[13px] text-ash">
            Files claimed by a saved source. They're cleaned up automatically
            when the source is deleted.
          </p>
        </div>
        {uploads.isLoading ? (
          <p className="px-6 py-8 text-sm text-ash">Loading…</p>
        ) : attached.length === 0 ? (
          <p className="px-6 py-8 text-[13px] text-ash">
            No attached files yet. The next file an operator drops onto a
            file-based connector's "Add source" form will land here.
          </p>
        ) : (
          <UploadTable rows={attached} sourceById={sourceById} kind="attached" />
        )}
      </section>

      <section className="card overflow-hidden">
        <div className="flex items-center justify-between gap-4 border-b border-rule px-6 py-4">
          <div>
            <h2 className="font-display text-[18px] font-semibold tracking-tight text-ink">
              Unattached
              <span className="ml-2 font-mono text-[12px] text-ash">{orphans.length}</span>
            </h2>
            <p className="mt-1 text-[13px] text-ash">
              Files uploaded but never saved against a source. The janitor
              removes anything older than 24h on its hourly sweep.
            </p>
          </div>
        </div>
        {orphans.length === 0 ? (
          <p className="px-6 py-8 text-[13px] text-ash">No orphans right now.</p>
        ) : (
          <UploadTable
            rows={orphans}
            sourceById={sourceById}
            kind="orphan"
            onDelete={(id) => remove.mutate(id)}
            deletingId={remove.isPending ? remove.variables : undefined}
          />
        )}
      </section>
    </div>
  );
}

function UploadTable({
  rows,
  sourceById,
  kind,
  onDelete,
  deletingId,
}: {
  rows: UploadListItem[];
  sourceById: Map<string, SourceLite>;
  kind: "attached" | "orphan";
  onDelete?: (id: string) => void;
  deletingId?: string;
}) {
  return (
    <table className="w-full text-sm">
      <thead className="border-b border-rule bg-paper text-left">
        <tr>
          <Th>File</Th>
          <Th>Size</Th>
          <Th>{kind === "attached" ? "Source" : "Uploaded"}</Th>
          <Th>SHA-256</Th>
          {kind === "orphan" ? <Th className="text-right">Actions</Th> : null}
        </tr>
      </thead>
      <tbody className="divide-y divide-rule">
        {rows.map((u) => {
          const src = u.sourceId ? sourceById.get(u.sourceId) : undefined;
          return (
            <tr key={u.id} className="transition-colors hover:bg-paper">
              <Td>
                <div className="font-semibold text-ink">{u.filename}</div>
                <div className="mt-0.5 font-mono text-[10.5px] text-ash">
                  {u.id}
                </div>
              </Td>
              <Td>
                <span className="font-mono text-[12px] text-ink">
                  {formatBytes(u.sizeBytes)}
                </span>
              </Td>
              <Td>
                {kind === "attached" ? (
                  src ? (
                    <a
                      href={`/sources/${encodeURIComponent(src.id)}`}
                      className="text-brand-700 hover:underline"
                    >
                      {src.name}
                      <span className="ml-1 font-mono text-[10.5px] text-ash">
                        {src.connectorId}
                      </span>
                    </a>
                  ) : (
                    <span className="font-mono text-[10.5px] text-ash">
                      {u.sourceId}
                    </span>
                  )
                ) : (
                  <span className="text-[12px] text-ash">
                    {new Date(u.createdAt).toLocaleString()}
                  </span>
                )}
              </Td>
              <Td>
                <span className="font-mono text-[10.5px] text-ash">
                  {u.sha256.slice(0, 12)}…
                </span>
              </Td>
              {kind === "orphan" && onDelete ? (
                <Td className="text-right">
                  <button
                    onClick={() => onDelete(u.id)}
                    disabled={deletingId === u.id}
                    className="btn-danger text-[12px]"
                  >
                    {deletingId === u.id ? "Deleting…" : "Delete now"}
                  </button>
                </Td>
              ) : null}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={
        "px-5 py-3 font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-ash " +
        (className ?? "")
      }
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={"px-5 py-4 align-top " + (className ?? "")}>{children}</td>;
}
